/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import clsx from 'clsx';
import {scrollToHash} from '../../utils/scrollToHash';
import {useEffect, useState} from 'react';
import {OkendoStarRating} from '../../routes/products/[handle].server';

export type StarRatingProps = {
  gid: string | undefined;
  stackStarsAndText?: {
    mobile?: boolean;
    md?: boolean;
  };
  isSmall?: boolean;
  scrollToAnchor?: boolean;
  align?: 'center' | 'left';
};

export const StarRating = (props: StarRatingProps) => {
  const [okendoStarRating, setOkendoStarRating] =
    useState<OkendoStarRating>(null);

  const productId = props.gid?.split('/').at(-1);

  useEffect(() => {
    async function getData() {
      try {
        const res = await fetch(`${origin}/api/okendo?productId=${productId}`);
        const data = await res.json();
        if (data) {
          setOkendoStarRating(data);
        }
      } catch (error) {
        console.error(error);
      }
    }

    getData();
  }, [productId]);

  if (!okendoStarRating) {
    if (!productId) {
      console.error('No product id found in gid', props.gid);
    }
  }

  const fullStarCount = okendoStarRating
    ? Math.floor(okendoStarRating.rating)
    : 0;
  const halfStar = okendoStarRating && okendoStarRating.rating % 1 !== 0;
  const emptyStarCount = okendoStarRating
    ? Math.floor(5 - okendoStarRating.rating)
    : 0;
  const stars = [];

  const width = props.isSmall ? 14 : 18;
  const height = props.isSmall ? 13 : 17;

  for (let i = 0; i < fullStarCount; i++) {
    stars.push(
      <Star key={`full-${i}`} type="full" width={width} height={height} />,
    );
  }

  if (halfStar) {
    stars.push(<Star key="half" type="half" width={width} height={height} />);
  }

  for (let i = 0; i < emptyStarCount; i++) {
    stars.push(
      <Star key={`empty-${i}`} type="empty" width={width} height={height} />,
    );
  }

  if (okendoStarRating?.reviewCount === 0) stars.pop();

  const isPlural = okendoStarRating?.reviewCount !== 1;

  return (
    <div
      className={clsx(
        'richH7 flex h-[24px] items-center',
        props.stackStarsAndText?.mobile ? 'flex-col' : 'flex-row',
        props.stackStarsAndText?.md
          ? 'md:flex-col'
          : 'md:flex-row md:justify-start',
        props.isSmall && props.align !== 'left' && 'md:pl-4',
        props.scrollToAnchor && 'cursor-pointer ',
        props.align === 'left' ? 'justify-start' : 'justify-center text-center',
      )}
      onClick={() => scrollToHash('reviews')}
    >
      <div
        className={clsx(
          'mt-[-2px] flex ',
          props.isSmall ? 'mr-0 w-[80px] gap-x-[2px] md:mr-1' : 'mr-2 gap-x-2',
        )}
      >
        <span className="sr-only">{okendoStarRating?.rating} stars</span>
        {stars}
      </div>

      {!!okendoStarRating?.reviewCount && !props.isSmall && (
        <span className="hidden sm:inline">Based on</span>
      )}

      <span
        className={clsx(
          'ml-1',
          props.stackStarsAndText?.mobile ? 'mt-2' : '',
          props.stackStarsAndText?.md ? 'md:mt-2' : 'md:mt-0',
          props.isSmall ? 'richH10 block w-full' : 'richH7 font-bold',
        )}
      >
        {!!okendoStarRating?.reviewCount &&
          `${okendoStarRating?.reviewCount} review${isPlural ? 's' : ''}`}
      </span>
    </div>
  );
};

type StarProps = {
  type: 'full' | 'half' | 'empty';
  width: number;
  height: number;
};

function Star(props: StarProps) {
  let fillRight = 'fill-orange';
  let fillLeft = 'fill-orange';

  switch (props.type) {
    case 'half':
      fillRight = 'fill-light-grey';
      break;
    case 'empty':
      fillRight = 'fill-light-grey';
      fillLeft = 'fill-light-grey';
      break;
  }

  return (
    <svg
      width={props.width}
      height={props.height}
      viewBox="0 0 12 11"
      className={''}
    >
      <path
        className={fillRight ? fillRight : ''}
        d="M4.7 1.9C5.3.6 5.5 0 6 0s.7.6 1.3 1.9V2c.3.7.5 1.1.8 1.3.3.2.7.3 1.6.3h.1c1.4.1 2 .2 2.2.6s-.4.8-1.4 1.7l-.3.3c-.5.5-.8.7-.9 1 0 .1 0 .1-.1.2 0 .2 0 .6.2 1.2v.2c.3 1.2.4 1.8.2 2-.1.1-.2.2-.3.2-.3.1-.8-.3-1.8-1-.6-.5-1-.7-1.3-.8h-.4c-.5 0-.8.2-1.5.7-1 .8-1.5 1.1-1.8 1-.1 0-.2-.1-.3-.2-.2-.3-.1-.8.2-2v-.2c.1-.6.2-1 .1-1.3 0-.1 0-.1-.1-.2 0-.1-.3-.3-.8-.8l-.3-.3C.4 5.1-.1 4.6 0 4.2c.1-.4.8-.5 2.2-.6h.1c.8-.1 1.2-.1 1.6-.3.3-.2.5-.6.8-1.4z"
      />
      <path
        className={fillLeft ? fillLeft : ''}
        d="M4.7 1.9c-.3.8-.5 1.2-.8 1.4-.3.2-.7.3-1.6.3h-.1c-1.4.2-2 .2-2.2.6-.1.4.4.8 1.4 1.7l.3.3c.5.4.8.7.9.9 0 .1 0 .1.1.2.1.3 0 .6-.1 1.3v.2c-.3 1.2-.4 1.8-.2 2 0 .1.1.2.2.2.3.1.8-.3 1.8-1 .6-.5 1-.7 1.3-.8h.2V0c-.4 0-.6.6-1.2 1.9z"
      />
    </svg>
  );
}
